import { Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  IComposableDashboardCollection,
  IComposableDashboardCollectionsContext,
  IComposableDashboardFiltersContext,
  IProfileDashboard,
  getMyProfileDashboards,
  fetchDashboardCollectionsAssignedToClients,
  fetchDashboardCollectionsAssignedToClientSpoc,
  fetchDashboardCollectionsAssignedToOrganisationalUnitAccountManagers,
  fetchDashboardCollectionsAssignedToOrganisationalUnitTeams,
  fetchDashboardCollectionsAssignedToTeams,
  fetchDashboardCollectionsAssignedToAccountManagers,
  fetchDashboardCollectionsAssignedToBusinessUnit,
  fetchDashboardCollectionsAssignedToUsers,
} from 'wisestep-api';
import { ScrollableTabs, WsColors, WsListingLoader } from 'wisestep-base';

import NoDataFoundCard from '../../../../components/noDataFoundCard/NoDataFoundCard';
import { useComposableDashboardCollectionsContext } from '../../../../features/analyticsPage/hooks/useComposableDashboardCollectionsContext';
import { useComposableDashboardsFiltersContext } from '../../../../features/analyticsPage/hooks/useComposableDashboardsFiltersContext';
import ErrorLogger from '../../../../providers/ErrorLogger';
import RenderDashboardsTabs from '../../../analyticsPage/components/dashboard/RenderDashboardsTabs';
import StageStatusContextProvider from '../../../submissions/context/StageStatusContext';

interface IUserProfileOverviewState {
  entitesTabList: any;
  profileDashboards: IProfileDashboard[];
}
interface IUserProfileOverviewAnalyticsTagging {
  entitesTabList: any;
  dashboardCollection: IComposableDashboardCollection[];
}

const UserProfileOverviewTemp = ({
  tabIndex,
  clientName,
  clientSpocEmail,
  accountManagersOU,
  teamsOU,
  teamIds,
  accountManager,
  businessUnitId,
  recruiterId,
  userId,
}: {
  tabIndex?: number;
  clientName?: string;
  clientSpocEmail?: string;
  accountManagersOU?: string[];
  teamsOU?: string[];
  teamIds?: string[];
  accountManager?: string;
  businessUnitId?: string;
  recruiterId?: string;
  userId?: string;
}) => {
  const {
    selectedDashboardCollection,
    setSelectedCollectionByIndex,
    dashboardCollections: masterDashboards,
  } = useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const [state, setState] = useState<IUserProfileOverviewState>({
    entitesTabList: undefined,
    profileDashboards: [],
  });

  const [stateForAnalyticsTagging, setStateForAnalyticsTagging] =
    useState<IUserProfileOverviewAnalyticsTagging>({
      entitesTabList: undefined,
      dashboardCollection: [],
    });

  const { overriddenFilters } =
    useComposableDashboardsFiltersContext() as Required<IComposableDashboardFiltersContext>;

  const handleSelectedTab = (index: number) => {
    if (
      clientName ||
      clientSpocEmail ||
      accountManagersOU ||
      teamsOU ||
      teamIds ||
      accountManager ||
      businessUnitId
    ) {
      const dbIndex = masterDashboards?.findIndex(
        (item) => item.id === stateForAnalyticsTagging.dashboardCollection[index]?.id
      );
      setSelectedCollectionByIndex(dbIndex, true);
    } else {
      const dbIndex = masterDashboards?.findIndex(
        (item) => item.id === state.profileDashboards[index]?.dashboardCollection?.id
      );
      setSelectedCollectionByIndex(dbIndex, true);
    }
  };

  const renderComponent = (tabList) =>
    tabList == undefined ? (
      <WsListingLoader />
    ) : !isEmpty(tabList) ? (
      <>
        {tabList?.length > 1 && (
          <Grid item xs={12} ml={-2}>
            <ScrollableTabs scrollList={tabList} getTabIndex={handleSelectedTab} divider={false} />
          </Grid>
        )}
        <StageStatusContextProvider>
          <RenderDashboardsTabs />
        </StageStatusContextProvider>
      </>
    ) : (
      <NoDataFoundCard
        text={
          clientName
            ? 'No Dashboards are tagged to this page'
            : 'No Dashboards are tagged to your profile'
        }
      />
    );

  useEffect(() => {
    const fetchMyProfileDashboards = async () => {
      try {
        let response;
        if (clientName) {
          response = await fetchDashboardCollectionsAssignedToClients(true);
          const tabs = response.data?.map((dashboardCollection, index) => ({
            tabName: dashboardCollection?.name,
            index: index,
          }));
          setStateForAnalyticsTagging({
            entitesTabList: tabs ?? [],
            dashboardCollection: response.data,
          });
        } else if (clientSpocEmail) {
          response = await fetchDashboardCollectionsAssignedToClientSpoc(true);
          const tabs = response.data?.map((dashboardCollection, index) => ({
            tabName: dashboardCollection?.name,
            index: index,
          }));
          setStateForAnalyticsTagging({
            entitesTabList: tabs ?? [],
            dashboardCollection: response.data,
          });
        } else if (accountManagersOU) {
          response = await fetchDashboardCollectionsAssignedToOrganisationalUnitAccountManagers(
            true
          );
          const tabs = response.data?.map((dashboardCollection, index) => ({
            tabName: dashboardCollection?.name,
            index: index,
          }));
          setStateForAnalyticsTagging({
            entitesTabList: tabs ?? [],
            dashboardCollection: response.data,
          });
        } else if (teamsOU) {
          response = await fetchDashboardCollectionsAssignedToOrganisationalUnitTeams(true);
          const tabs = response.data?.map((dashboardCollection, index) => ({
            tabName: dashboardCollection?.name,
            index: index,
          }));
          setStateForAnalyticsTagging({
            entitesTabList: tabs ?? [],
            dashboardCollection: response.data,
          });
        } else if (teamIds) {
          response = await fetchDashboardCollectionsAssignedToTeams(true);
          const tabs = response.data?.map((dashboardCollection, index) => ({
            tabName: dashboardCollection?.name,
            index: index,
          }));
          setStateForAnalyticsTagging({
            entitesTabList: tabs ?? [],
            dashboardCollection: response.data,
          });
        } else if (accountManager) {
          response = await fetchDashboardCollectionsAssignedToAccountManagers(true);
          const tabs = response.data?.map((dashboardCollection, index) => ({
            tabName: dashboardCollection?.name,
            index: index,
          }));
          setStateForAnalyticsTagging({
            entitesTabList: tabs ?? [],
            dashboardCollection: response.data,
          });
        } else if (businessUnitId) {
          response = await fetchDashboardCollectionsAssignedToBusinessUnit(true);
          const tabs = response.data?.map((dashboardCollection, index) => ({
            tabName: dashboardCollection?.name,
            index: index,
          }));
          setStateForAnalyticsTagging({
            entitesTabList: tabs ?? [],
            dashboardCollection: response.data,
          });
        } else if (userId) {
          response = await fetchDashboardCollectionsAssignedToUsers(true);
          const tabs = response.data?.map((dashboardCollection, index) => ({
            tabName: dashboardCollection?.name,
            index: index,
          }));
          setStateForAnalyticsTagging({
            entitesTabList: tabs ?? [],
            dashboardCollection: response.data,
          });
        } else {
          response = await getMyProfileDashboards();
          const tabs = response.data?.map((pd, index) => ({
            tabName: pd.dashboardCollection?.name,
            index: index,
          }));
          setState({ entitesTabList: tabs ?? [], profileDashboards: response.data });
        }
      } catch {
        setState({ entitesTabList: [], profileDashboards: [] });
      }
    };
    fetchMyProfileDashboards();
  }, [
    accountManager,
    accountManagersOU,
    clientName,
    clientSpocEmail,
    teamIds,
    teamsOU,
    businessUnitId,
    userId,
  ]);

  useEffect(() => {
    if (selectedDashboardCollection?.id == undefined) {
      if (
        clientName ||
        clientSpocEmail ||
        accountManagersOU ||
        teamsOU ||
        teamIds ||
        accountManager ||
        businessUnitId ||
        userId
      ) {
        const dbIndex = masterDashboards?.findIndex(
          (item) => item.id === stateForAnalyticsTagging.dashboardCollection?.[0]?.id
        );
        setSelectedCollectionByIndex(dbIndex, true);
      } else {
        const dbIndex = masterDashboards?.findIndex(
          (item) => item.id === state.profileDashboards?.[0]?.dashboardCollection?.id
        );
        setSelectedCollectionByIndex(dbIndex, true);
      }
    }
  }, [
    state.profileDashboards,
    masterDashboards,
    setSelectedCollectionByIndex,
    selectedDashboardCollection?.id,
    clientName,
    stateForAnalyticsTagging.dashboardCollection,
    clientSpocEmail,
    accountManagersOU,
    teamsOU,
    teamIds,
    accountManager,
    businessUnitId,
    userId,
  ]);

  const applyFilters = useCallback(() => {
    const conditionsToFilters = [
      { condition: clientName, filter: { clientNames: [clientName] } },
      { condition: clientSpocEmail, filter: { clientSpocEmails: [clientSpocEmail] } },
      { condition: accountManagersOU, filter: { accountManagers: accountManagersOU } },
      { condition: teamsOU, filter: { teamIds: teamsOU } },
      { condition: teamIds, filter: { teamIds: teamIds } },
      { condition: accountManager, filter: { accountManagers: [accountManager] } },
      { condition: businessUnitId, filter: { businessUnitId: businessUnitId } },
      { condition: recruiterId, filter: { recruiterNames: [recruiterId] } },
      { condition: userId, filter: { recruiterNames: [userId ?? ''] } },
    ];
    const { filter = {} } = conditionsToFilters.find(({ condition }) => condition) || {};
    return filter;
  }, [
    clientName,
    clientSpocEmail,
    accountManagersOU,
    teamsOU,
    teamIds,
    accountManager,
    businessUnitId,
    recruiterId,
    userId,
  ]);

  useEffect(() => {
    overriddenFilters.current = applyFilters();
  }, [applyFilters, overriddenFilters]);

  useEffect(() => {
    if (tabIndex != 1) setSelectedCollectionByIndex(-1);
  }, [setSelectedCollectionByIndex, tabIndex]);

  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Grid bgcolor={WsColors.White} mt={2}>
        {clientName ||
        clientSpocEmail ||
        accountManagersOU ||
        teamsOU ||
        teamIds ||
        accountManager ||
        businessUnitId ||
        userId
          ? renderComponent(stateForAnalyticsTagging.entitesTabList)
          : renderComponent(state.entitesTabList)}
      </Grid>
    </ErrorBoundary>
  );
};
export default UserProfileOverviewTemp;
